<template>
  <div class="photography-gallery">
    <SideBar />
    <header class="gallery-header">
      <div class="header-background">
        <div class="light-beam"></div>
        <div class="light-beam delay-1"></div>
        <div class="light-beam delay-2"></div>
      </div>

      <div class="header-content">
        <div class="title-section">
          <h1 class="main-title">我无法回答为什么要去记录这些</h1>
          <h1 class="main-title">时间是握不住的，我只好将其尽可能的留下来！</h1>
        </div>

        <div class="filter-section">
          <div class="filter-buttons">
            <div v-for="tag in tags" :key="tag.id" class="filter-button" :class="{ 'active': typeId === tag.id }"
              @click="clickTag(tag)">
              <span class="filter-text">{{ tag.name }}</span>
              <span class="filter-count">{{ tag.count }}</span>
              <div class="filter-glow"></div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="gallery-content">
      <div v-if="MuseumDataList.length === 0 && first" class="art-skeleton">
        <div class="skeleton-container">
          <div v-for="n in 8" :key="n" class="skeleton-card" :style="{ animationDelay: `${n * 0.1}s` }">
            <div class="skeleton-image"></div>
            <div class="skeleton-info">
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 稳定瀑布流容器 -->
      <div v-else class="masonry-wrapper" ref="masonryWrapper">
        <div class="masonry-column" v-for="col in columnCount" :key="col" :style="{ padding: `0 ${gutter / 2}px` }">
          <div v-for="(item, index) in getColumnItems(col - 1)" :key="item.id" class="masonry-item"
            :class="{ 'loaded': item.imageLoaded }" @click="viewPhoto(item)">
            <div class="photo-frame">
              <div class="progressive-layer">

                <img :src="baseFileUrl + item.img" :alt="item.name" class="photo-image"
                  :class="{ 'loaded': item.imageLoaded }" loading="lazy" @load="onImageLoad(item)" />

                <div v-if="!item.imageLoaded" class="loading-state">
                  <div class="loading-circle">
                    <div class="loading-inner"></div>
                  </div>
                </div>
              </div>

              <div class="frame-border top"></div>
              <div class="frame-border right"></div>
              <div class="frame-border bottom"></div>
              <div class="frame-border left"></div>
            </div>

            <div class="photo-caption">
              <h4>{{ item.name }}</h4>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="load-more">
        <div class="filmstrip-loading">
          <div class="filmstrip-frame" v-for="n in 3" :key="n" :style="{ animationDelay: `${n * 0.2}s` }"></div>
        </div>
        <span class="loading-text">正在加载更多作品...</span>
      </div>

      <div v-if="noMoreData && MuseumDataList.length > 0" class="end-section">
        <div class="end-line"></div>
        <div class="end-content">
          <svg class="end-icon" viewBox="0 0 24 24">
            <path
              d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
          </svg>
          <span class="end-text">已展示所有 {{ typeName }} 作品</span>
        </div>
        <div class="end-line"></div>
      </div>
    </main>

    <div v-if="isFullscreen" class="fullscreen-viewer" @click.self="closeFullscreen">
      <div class="viewer-container">
        <div class="viewer-controls">
          <button class="control-btn close-btn" @click="closeFullscreen">
            <svg viewBox="0 0 24 24">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div class="image-container">
          <img :src="currentFullscreenImage" :alt="currentImageName" class="fullscreen-image"
            :class="{ 'loaded': fullscreenImageLoaded }" @load="onFullscreenImageLoad" />

          <div v-if="!fullscreenImageLoaded" class="fullscreen-loading">
            <div class="aperture-loader">
              <div class="aperture-blade" v-for="n in 6" :key="n"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <footer class="gallery-footer">
      <BottomTip />
    </footer>
  </div>
</template>

<script>
import config from '@/utils/Config';
import { getMuseumDataList, getAlbumTypeList } from '@/api/album';
import SideBar from '@/components/menu/SideBar.vue';
import BottomTip from '@/components/menu/BottomTip.vue';

export default {
  name: 'Nature',
  components: { SideBar, BottomTip },
  data() {
    return {
      baseFileUrl: config.BASE_FILE_URL,
      MuseumDataList: [],
      isFullscreen: false,
      currentFullscreenImage: '',
      currentImageName: '',
      currentImageDate: '',
      fullscreenImageLoaded: false,

      pageNo: 1,
      pageSize: 8,
      loading: false,
      noMoreData: false,
      first: true,

      typeId: 6,
      typeName: '全部作品',
      tags: [],

      columnCount: 3,
      gutter: 24,
      resizeTimer: null
    };
  },
  async mounted() {
    this.initColumns();
    await this.initTags();
    await this.loadPhotos();
    this.setupEventListeners();
    this.startHeaderAnimation();

    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    this.removeEventListeners();
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initColumns() {
      const w = window.innerWidth;
      if (w < 480) this.columnCount = 1;
      else if (w < 992) this.columnCount = 2;
      else this.columnCount = 3;
    },
    getColumnItems(colIndex) {
      return this.MuseumDataList.filter((_, idx) => idx % this.columnCount === colIndex);
    },

    onImageLoad(item) {
      item.imageLoaded = true;
    },

    async initTags() {
      try {
        const resp = await getAlbumTypeList();
        if (resp.code == 200 && resp.data.length > 0) {
          this.tags = resp.data;
          this.typeId = this.tags[0].id;
          this.typeName = this.tags[0].name;
        }
      } catch (e) {
        this.tags = [{ id: 0, name: '全部', count: 0 }];
      }
    },
    async loadPhotos(isLoadMore = false) {
      if (this.loading || this.noMoreData) return;
      this.loading = true;

      try {
        const params = {
          userId: config.BASE_USER_ID,
          typeId: this.typeId === 0 ? '' : this.typeId,
          pageNo: this.pageNo,
          pageSize: this.pageSize
        };
        const resp = await getMuseumDataList(params);

        if (resp.code == 200 && resp.data.data.length > 0) {
          const newItems = resp.data.data.map(item => ({
            ...item, imageLoaded: false
          }));

          if (isLoadMore) this.MuseumDataList.push(...newItems);
          else this.MuseumDataList = newItems;

          if (resp.data.data.length < this.pageSize) this.noMoreData = true;
          else this.pageNo++;
        } else {
          if (!isLoadMore) this.MuseumDataList = [];
          this.noMoreData = true;
        }
      } catch (e) {
        console.error('加载失败', e);
      } finally {
        this.loading = false;
        this.first = false;
      }
    },

    clickTag(tag) {
      this.typeId = tag.id;
      this.typeName = tag.id === 0 ? '全部作品' : tag.name + '作品';
      this.resetPagination();
      this.loadPhotos();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    viewPhoto(item) {
      this.currentFullscreenImage = this.baseFileUrl + item.img;
      this.currentImageName = item.name;
      this.currentImageDate = this.formatDate(item.createTime);
      this.fullscreenImageLoaded = false;
      this.isFullscreen = true;
      document.body.style.overflow = 'hidden';
    },
    closeFullscreen() {
      this.isFullscreen = false;
      document.body.style.overflow = '';
    },
    onFullscreenImageLoad() {
      this.fullscreenImageLoaded = true;
    },

    formatDate(s) {
      if (!s) return '';
      return new Date(s).toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
    },

    setupEventListeners() {
      this.scrollFn = this.throttle(() => {
        if (this.loading || this.noMoreData) return;
        const h = window.innerHeight;
        const top = document.documentElement.scrollTop || document.body.scrollTop;
        const height = document.documentElement.scrollHeight;
        if (top + h + 300 >= height) this.loadPhotos(true);
      }, 200);
      window.addEventListener('scroll', this.scrollFn, { passive: true });
    },
    handleResize() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.initColumns(), 300);
    },
    throttle(fn, t) {
      let timer = null;
      return (...args) => {
        if (!timer) {
          fn.apply(this, args);
          timer = setTimeout(() => timer = null, t);
        }
      };
    },

    resetPagination() {
      this.pageNo = 1;
      this.noMoreData = false;
      this.MuseumDataList = [];
    },
    startHeaderAnimation() { },
    handleHeaderScroll() { },
    removeEventListeners() {
      window.removeEventListener('scroll', this.scrollFn);
    }
  }
};
</script>

<style scoped>
.photography-gallery {
  min-height: 100vh;
  color: #e2e8f0;
  position: relative;
  overflow: hidden;
}

.gallery-header {
  position: relative;
  padding: 4rem 2rem 3rem;
  background: linear-gradient(to bottom,
      rgba(15, 23, 42, 0.8) 0%,
      rgba(15, 23, 42, 0.6) 50%,
      rgba(15, 23, 42, 0.3) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  z-index: 100;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
}

.light-beam {
  position: absolute;
  top: -100%;
  left: 20%;
  width: 2px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, rgba(59, 130, 246, 0.3), transparent);
  animation: beam-sweep 8s linear infinite;
}

.light-beam.delay-1 {
  left: 50%;
  animation-delay: -2s;
}

.light-beam.delay-2 {
  left: 80%;
  animation-delay: -5s;
}

@keyframes beam-sweep {
  0% {
    transform: translateY(-100%) rotate(10deg);
    opacity: 0;
  }

  10% {
    opacity: 0.5;
  }

  90% {
    opacity: 0.5;
  }

  100% {
    transform: translateY(200%) rotate(10deg);
    opacity: 0;
  }
}

.header-content {
  max-width: 1400px;
  margin: 0 auto;
}

.title-section {
  text-align: center;
  margin-bottom: 2rem;
}

.main-title {
  font-size: 20px;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-family: 'Playfair Display', serif;

  background: linear-gradient(90deg, #ff0080, #ff8c00, #40e0d0, #9370db, #ff0080);
  background-size: 400% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientMove 8s ease-in-out infinite;
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }

  100% {
    background-position: 0% 50%;
  }
}

.subtitle {
  font-size: 1.1rem;
  color: #94a3b8;
  letter-spacing: 3px;
}

.filter-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.2rem;
}

.filter-button {
  position: relative;
  padding: 0.3rem 1rem;
  background: rgba(30, 41, 59, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.filter-button:hover {
  background: linear-gradient(90deg, #e5398f, #be2a79);
  border-color: rgba(59, 130, 246, 0.4);
}

.filter-button.active {
  background: linear-gradient(90deg, #e5398f, #be2a79);
  border-color: rgba(59, 130, 246, 0.6);
}

.filter-text {
  color: #cbd5e1;
  font-size: 0.95rem;
}

.filter-count {
  font-size: 0.85rem;
  color: #94a3b8;
  padding: 0.25rem;
}

.filter-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(59, 130, 246, 0.3), transparent 70%);
  opacity: 0;
  transition: opacity 0.3s;
}

.filter-button.active .filter-glow {
  opacity: 1;
}

.gallery-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 2rem;
}

.art-skeleton {
  padding: 2rem 0;
}

.skeleton-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 2rem;
}

.skeleton-card {
  animation: skeleton-pulse 1.5s infinite;
}

@keyframes skeleton-pulse {

  0%,
  100% {
    opacity: 0.6;
  }

  50% {
    opacity: 0.3;
  }
}

.skeleton-image {
  width: 100%;
  aspect-ratio: 4/3;
  background: #cbb89c;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.masonry-wrapper {
  display: flex;
  width: 100%;
  margin: 0 auto;
}

.masonry-column {
  flex: 1;
}

.masonry-item {
  margin-bottom: 24px;
  opacity: 0;
  transform: translateY(30px);
  animation: appear 0.6s forwards;
}

.masonry-item.loaded {
  opacity: 1;
}

@keyframes appear {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.photo-frame {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(15, 23, 42, 0.5);
  box-shadow:
    7px 7px 10px rgba(0, 0, 0, .9),
    -5px -5px 10px rgba(239, 235, 235, 0.3);
  max-width: 92%;
  margin: 0 auto;
}

.photo-frame:hover .photo-image {
  transform: scale(1.08);
}

.photo-image {
  width: 100%;
  height: auto;
  display: block;
  opacity: 0;
  transition: opacity 0.5s ease, transform 0.8s cubic-bezier(0.19, 1, 0.22, 1);
}

.photo-image.loaded {
  opacity: 1;
}

.progressive-layer {
  position: relative;
  width: 100%;
  height: auto;
  overflow: hidden;
}

.loading-state {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #cbb89c;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
}

.loading-circle {
  width: 40px;
  height: 40px;
  position: relative;
}

.loading-inner {
  width: 100%;
  height: 100%;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: rotate 1s linear infinite;
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

.frame-border {
  position: absolute;
  background: rgba(59, 130, 246, 0.1);
  z-index: 5;
}

.frame-border.top,
.frame-border.bottom {
  left: 12px;
  right: 12px;
  height: 1px;
}

.frame-border.left,
.frame-border.right {
  top: 12px;
  bottom: 12px;
  width: 1px;
}

.frame-border.top {
  top: 0;
}

.frame-border.right {
  right: 0;
}

.frame-border.bottom {
  bottom: 0;
}

.frame-border.left {
  left: 0;
}

.photo-caption {
  padding: 0.8rem 0.2rem;
  text-align: center;
}

.photo-caption h4 {
  margin: 0;
  font-size: 0.9rem;
  color: var(--font-color-6);
  font-weight: 400;
}

.load-more {
  text-align: center;
  padding: 3rem 0;
}

.filmstrip-loading {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.filmstrip-frame {
  width: 40px;
  height: 50px;
  background: linear-gradient(135deg, #1e293b, #334155);
  border-radius: 4px;
  animation: filmstrip 1.5s infinite;
}

@keyframes filmstrip {

  0%,
  100% {
    transform: translateY(0);
    opacity: 0.6;
  }

  50% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

.loading-text {
  font-size: 0.9rem;
  color: #94a3b8;
  margin-top: 1rem;
}

.end-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 4rem 0;
}

.end-line {
  width: 80px;
  height: 1px;
  background: linear-gradient(to right, transparent, #475569, transparent);
}

.end-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.end-icon {
  width: 24px;
  height: 24px;
  fill: #64788b;
}

.end-text {
  font-size: 0.9rem;
  color: #94a3b8;
}

.fullscreen-viewer {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(20px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.viewer-container {
  width: 95%;
  height: 95%;
  position: relative;
  display: flex;
  flex-direction: column;
}

.viewer-controls {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 1rem;
  z-index: 10;
}

.control-btn {
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.695);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s;
}

.control-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.image-container {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.fullscreen-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
}

.fullscreen-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.aperture-loader {
  width: 60px;
  height: 60px;
  position: relative;
}

.aperture-blade {
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 30px;
  background: #60a5fa;
  transform-origin: 50% 100%;
  animation: aperture 2s infinite;
}

@keyframes aperture {

  0%,
  100% {
    opacity: 0.3;
    transform: rotate(0deg) translateY(0);
  }

  50% {
    opacity: 1;
    transform: rotate(0deg) translateY(-10px);
  }
}

.image-info {
  padding: 1.5rem;
  background: rgba(15, 23, 42, 0.8);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.info-title {
  margin: 0 0 0.5rem;
  font-size: 1.5rem;
  color: #f1f5f9;
}

.info-meta {
  color: #94a3b8;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.meta-icon {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.gallery-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 1024px) and (min-width: 768px) {
  .gallery-content {
    padding: 2.5rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .gallery-header {
    padding: 4rem 1rem 2rem;
  }

  .main-title {
    font-size: 18px;
    letter-spacing: 2px;
  }

  .filter-button {
    padding: 0.25rem 0.7rem;
  }

  .gallery-content {
    padding: 1.5rem 1rem;
  }

  .photo-frame {
    max-width: 100%;
  }

  .masonry-item {
    margin-bottom: 16px;
  }
}
</style>